<script setup>
import axios from 'axios';
import { BAlert, BButton, BLink } from 'bootstrap-vue-next';
import { computed, ref, onMounted } from 'vue';

import ErrorState from '~/common/components/ErrorState.vue';
import MarkdownRenderer from '~/common/components/MarkdownRenderer.vue';
import ThemedOverlay from '~/common/components/ThemedOverlay.vue';
import { toUTCDate } from '~/common/DateUtils';
import { useVueData } from '~/common/useVueData';

import aboutMarkdown from './hackathon-page/AboutSection.md?raw';
import CountdownTimer from './hackathon-page/CountdownTimer.vue';
import DetailsSection from './hackathon-page/DetailsSection.vue';
import HeaderSection from './hackathon-page/HeaderSection.vue';
import LeaderboardSection from './hackathon-page/LeaderboardSection.vue';
import PastEventsSection from './hackathon-page/PastEventsSection.vue';
import TermsAcceptanceModal from './hackathon-page/TermsAcceptanceModal.vue';

const { isSignedIn } = useVueData();

const currentHackathon = ref({});
const loading = ref(true);
const error = ref(null);
const termsModalVisible = ref(false);
const registered = ref(null);
const disqualified = ref(false);
const leaderboardKey = ref(0);

const fetchCalendar = async () => {
  loading.value = true;
  error.value = null;
  try {
    const response = await axios.get('/api/v1/hackathons/current');
    currentHackathon.value = response.data;
  } catch (e) {
    error.value = e;
  } finally {
    loading.value = false;
  }
};

const today = new Date();

const transformedHackathonData = computed(() => {
  return {
    displayName: currentHackathon.value.display_name,
    startDate: toUTCDate(currentHackathon.value.from_date),
    endDate: toUTCDate(currentHackathon.value.to_date),
    mergeDate: toUTCDate(currentHackathon.value.merge_date),
  };
});

const startDate = computed(() => transformedHackathonData.value.startDate);
const endDate = computed(() => transformedHackathonData.value.endDate);
const mergeDate = computed(() => transformedHackathonData.value.mergeDate);

const isHackathonActive = computed(() => !loading.value && !error.value && today >= startDate.value && today <= mergeDate.value);

const checkRegistration = async () => {
  if (!isSignedIn || !isHackathonActive.value) {
    return;
  }

  try {
    const { data } = await axios.get('/api/v1/hackathon_registrations');
    registered.value = data.registered;
    disqualified.value = data.disqualified;
    termsModalVisible.value = !data.registered && !data.disqualified;
  } catch {
    return;
  }
};

const showDisqualifiedNotice = computed(() => isSignedIn && isHackathonActive.value && disqualified.value);
const showAcceptAlert = computed(() => (
  isSignedIn && isHackathonActive.value && registered.value === false && !disqualified.value
));
const showAcceptedNotice = computed(() => (
  isSignedIn && isHackathonActive.value && registered.value === true && !disqualified.value
));

const onAccepted = () => {
  registered.value = true;
  leaderboardKey.value += 1;
};

const onDisqualified = () => {
  disqualified.value = true;
  termsModalVisible.value = false;
  leaderboardKey.value += 1;
};

onMounted(async () => {
  await fetchCalendar();
  checkRegistration();
});
</script>

<template>
  <ErrorState v-if="error" />
  <ThemedOverlay
    v-else
    :show="loading"
  >
    <HeaderSection
      class="mt-3 mb-2"
    />
    <CountdownTimer
      v-if="!loading"
      :start-date="startDate"
      :end-date="endDate"
      :merge-date="mergeDate"
    />
    <p>
      This is the Community Hackathon, rewarded with GitLab swag credits.
      Looking to compete for cash prizes instead? Head to the
      <BLink :to="{ name: 'transcend-hackathon' }">
        Transcend Hackathon
      </BLink>.
    </p>
    <MarkdownRenderer :markdown-content="aboutMarkdown" />
    <div class="mb-3">
      <BLink href="#past-events">
        View past events
      </BLink>
    </div>
    <DetailsSection />
    <BAlert
      v-if="showDisqualifiedNotice"
      :model-value="true"
      variant="danger"
      class="mb-3"
    >
      You have been disqualified from this hackathon and your contributions will not count towards it.
      If you think this is a mistake, contact us in the
      <BLink href="https://discord.gg/gitlab">GitLab Discord</BLink>.
    </BAlert>
    <BAlert
      v-if="showAcceptAlert"
      :model-value="true"
      variant="warning"
      class="mb-3"
    >
      Your contributions will not count towards the hackathon until you
      <BButton
        variant="link"
        class="p-0 align-baseline"
        @click="termsModalVisible = true"
      >
        accept the rules.
      </BButton>
    </BAlert>
    <BAlert
      v-if="showAcceptedNotice"
      :model-value="true"
      variant="success"
      is-status
      class="mb-3"
    >
      You have accepted the
      <BLink :to="{ name: 'documentation', params: { page: 'hackathon-rules' } }">hackathon rules</BLink>.
    </BAlert>
    <LeaderboardSection
      v-if="!loading && startDate <= today"
      :key="`leaderboard-${leaderboardKey}`"
      :current-hackathon="transformedHackathonData"
    />
    <PastEventsSection id="past-events" />
    <TermsAcceptanceModal
      v-model="termsModalVisible"
      @accepted="onAccepted"
      @disqualified="onDisqualified"
    />
  </ThemedOverlay>
</template>
